// ./src/components/features/CreateEventWizard/steps/QuickTicketStep.tsx
//
// #148 — the quick ticket sale's "Your tickets" screen: what one ticket is called, what it costs (in which currency),
// and how many there are. Shown on the `tickets` step of the QUICK path only; the full wizard's Tickets step
// (`TicketsStep`) needs the event to exist already, and the quick path creates it on Finish.
//
// Nothing is written here. Finish creates the event (its currency, and "how many" as its CAPACITY — the places the
// dashboard and the checkout count) and then the ticket type (`quickEventInput`, `quickTicketInput`).
//
// ⚠ WHAT IS MISSING IS SAID HERE, BEFORE FINISH (owner): the plan's ticketing (Next stays off — a ticket could not be
//   made), and card payments for a ticket that costs money (Finish then keeps the event a DRAFT: nobody could pay).
// ⚠ PRESENCE ONLY. The server owns the formats and ranges; its refusal of a ticket brings the host back here, under the
//   input it names (`saveError`).
// ⚠ The currency starts as the server would choose it: the account's event default, else its billing currency. Asked
//   rather than left to the server, because the host is typing a PRICE in it — and it locks once money is recorded.
import React, { useEffect, useMemo, useRef } from 'react';
import { Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { FEATURE_CODES } from '@/config';
import { useApp, useAppEnvironment } from '@/contexts';
import { useOptionalAccountPreferenceContext } from '@/contexts/AccountPreferenceContext';
import { useCreateGate } from '@/hooks';
import { Account, AccountService } from '@/utils/api';
import { getFieldError } from '@/utils';
import { currencySelectOptions } from '@/utils/eventSelling';
import { NumberField, PlanFeatureNotice, SelectField, TextField, currencyAffixes } from '@/components/generic';

import { useEventWizard } from '../WizardContext';
import { CardPaymentsNotice } from '@/components/features/CardPayments';
import { EVENT_FORMAT, EventWizardDraft, QuickTicketDraft } from '../wizardTypes';

const EMPTY_TICKET: QuickTicketDraft = { name: '', price: null, currencyCode: null };

/** The ticket with `patch` applied — to the LATEST draft (an updater), so two quick patches never undo each other. */
const ticketPatch =
  (patch: Partial<QuickTicketDraft> | ((ticket: QuickTicketDraft) => Partial<QuickTicketDraft>)) =>
  (prev: EventWizardDraft): Partial<EventWizardDraft> => {
    const ticket = prev.quickTicket ?? EMPTY_TICKET;

    return { quickTicket: { ...ticket, ...(typeof patch === 'function' ? patch(ticket) : patch) } };
  };

export const QuickTicketStep: React.FC = () => {
  const { t } = useTranslation(['events', 'common']);
  const { isMobile } = useAppEnvironment();
  const { user } = useApp();
  const { draft, patchDraft, saving, saveError, cardPayments } = useEventWizard();
  const accountPreference = useOptionalAccountPreferenceContext();
  const ticketGate = useCreateGate(FEATURE_CODES.TICKETING);
  const currencyData = useMemo(() => currencySelectOptions(), []);

  const ticket: QuickTicketDraft = draft.quickTicket ?? EMPTY_TICKET;

  const patchTicket = (patch: Partial<QuickTicketDraft>) => {
    patchDraft(ticketPatch(patch));
  };

  // A first ticket with the usual name, priced in the account's event currency when it has one.
  const preferred = accountPreference?.preference?.defaultCurrencyCode ?? null;

  useEffect(() => {
    if (!draft.quickTicket) {
      patchDraft((prev) =>
        prev.quickTicket
          ? {}
          : { quickTicket: { name: t('events:wizard.tickets.suggest.general', { defaultValue: 'General admission' }), price: null, currencyCode: preferred } },
      );
    } else if (!draft.quickTicket.currencyCode && preferred) {
      patchDraft(ticketPatch((current) => (current.currencyCode ? {} : { currencyCode: preferred })));
    }
  }, [draft.quickTicket, preferred, patchDraft, t]);

  // No event default → the account's BILLING currency (the server's next choice), read once. ⚠ Only into an empty
  // choice: never over one the host made while it was being read.
  const preferenceSettled = !accountPreference || accountPreference.isLoaded || !!accountPreference.lastError;
  const accountId = user?.account?.id ?? null;
  const billingReadRef = useRef(false);

  const chosen = !!ticket.currencyCode;

  useEffect(() => {
    if (billingReadRef.current || !preferenceSettled || preferred || !accountId || chosen) {
      return;
    }

    billingReadRef.current = true;

    void AccountService.get(accountId).then((res) => {
      const currency = res.didSucceed() ? (res.getDataFirst() as Account | undefined)?.currency : null;

      if (currency) {
        patchDraft(ticketPatch((current) => (current.currencyCode ? {} : { currencyCode: currency })));
      }
    });
  }, [preferenceSettled, preferred, accountId, chosen, patchDraft]);

  const paid = (ticket.price ?? 0) > 0;
  // ⓘ Only while the event is AT that venue: an online event sends no venue, so "from the hall" would be untrue.
  const atVenue = draft.eventFormat === EVENT_FORMAT.IN_PERSON || draft.eventFormat === EVENT_FORMAT.HYBRID;
  const fromVenue = atVenue && !!draft.capacityFromVenueId && draft.capacityFromVenueId === draft.venueId && !!draft.venueName;

  return (
    <Stack gap="lg">
      <Text size="md" c="dimmed">
        {t('events:wizard.quickTickets.intro', {
          defaultValue: 'What one ticket is called, what it costs, and how many you are selling. You can add more ticket types later.',
        })}
      </Text>

      {/* The plan refuses the ticket — said BEFORE Finish, which could not make it. */}
      <PlanFeatureNotice
        verdict={ticketGate.verdict}
        onUpgrade={ticketGate.goToUpgrade}
        what={t('events:wizard.quickTickets.what', { defaultValue: 'tickets' })}
      />

      <TextField
        name="name"
        label={t('events:wizard.quickTickets.name', { defaultValue: 'Ticket name' })}
        value={ticket.name}
        onChange={(_name, value) => patchTicket({ name: String(value ?? '') })}
        required
        withAsterisk
        disabled={saving}
        error={getFieldError(saveError, 'name')}
      />

      <SelectField
        name="currencyCode"
        label={t('events:details.currency', { defaultValue: 'Currency' })}
        description={t('events:details.currencyHint', {
          defaultValue: 'Everything on this event is priced in it. Locks once money is recorded.',
        })}
        data={currencyData}
        value={ticket.currencyCode ?? ''}
        onChange={(_name, value) => patchTicket({ currencyCode: (value as string) || null })}
        searchable
        required
        allowDeselect={false}
        disabled={saving}
        error={getFieldError(saveError, 'currencyCode')}
      />

      <NumberField
        name="price"
        label={t('events:wizard.quickTickets.price', { defaultValue: 'Price' })}
        description={t('events:wizard.quickTickets.priceHint', { defaultValue: '0 makes the ticket free.' })}
        value={ticket.price}
        onChange={(_name, value) => patchTicket({ price: value })}
        min={0}
        clampBehavior="strict"
        decimalScale={2}
        required
        withAsterisk
        disabled={saving}
        w={isMobile ? '100%' : 240}
        error={getFieldError(saveError, 'price')}
        {...currencyAffixes(ticket.currencyCode)}
      />

      <NumberField
        name="capacity"
        label={t('events:wizard.quickTickets.howMany', { defaultValue: 'How many tickets' })}
        description={
          fromVenue
            ? t('events:wizard.quickTickets.howManyVenue', {
                defaultValue: 'Filled in from {{venue}} — change it if you are selling fewer.',
                venue: draft.venueName,
              })
            : t('events:wizard.quickTickets.howManyHint', { defaultValue: 'Sales stop when they are gone.' })
        }
        value={draft.capacity}
        // ⚠ Typing a number makes it the host's: the venue no longer owns it (`venueCapacityPatch`).
        onChange={(_name, value) => patchDraft({ capacity: value, capacityFromVenueId: null })}
        min={1}
        clampBehavior="strict"
        decimalScale={0}
        required
        withAsterisk
        disabled={saving}
        w={isMobile ? '100%' : 240}
        error={getFieldError(saveError, 'capacity')}
      />

      <CardPaymentsNotice
        payments={cardPayments}
        paid={paid}
        offBody={t('events:wizard.quickTickets.cardsOff', {
          defaultValue:
            'Free tickets work now. To sell paid tickets online, connect your Stripe account — the money goes straight to you. Until then, Finish saves your event as a draft instead of publishing it.',
        })}
        unknownBody={
          paid
            ? t('events:wizard.quickTickets.cardsUnknown', {
                defaultValue: 'We could not check whether card payments are set up, so Finish would save your event as a draft. Check again before you finish.',
              })
            : undefined
        }
      />
    </Stack>
  );
};
